import type { CaseData } from "@/cms/models";
import { getCompactPropertyFacts } from "./compact-property-facts";
import { formatNumber } from "./number-format";

export interface ClassicPropertyFact {
  key: string;
  label: string;
  value: string;
}

/** Share the established Gallery estate policy; only the presentation differs. */
export function getClassicPropertyFacts(
  property: CaseData,
  detail = false,
): ClassicPropertyFact[] {
  const compact = getCompactPropertyFacts(property);
  const facts: ClassicPropertyFact[] = [
    {
      key: "estate",
      label: "Boligtype",
      value: property.estateType || "Ejendom",
    },
  ];
  const areaParts = [
    { label: "Bolig", value: property.housingArea },
    { label: "erhv.", value: property.commercialArea },
    { label: "kld.", value: property.basementArea },
  ].filter((part) => part.value > 0);
  if (["land", "residential", "agriculture"].includes(compact.family)) {
    if (areaParts.length && compact.family !== "land")
      facts.push({
        key: "areas",
        label: areaParts.map((part) => part.label).join(" / "),
        value: `${areaParts.map((part) => formatNumber(part.value)).join(" / ")} m²`,
      });
    if (property.plotArea > 0)
      facts.push({
        key: "plot",
        label: "Grund",
        value: `${formatNumber(property.plotArea)} m²`,
      });
  } else {
    facts.push(
      ...compact.facts
        .filter((fact) => fact.key !== "estateType" && fact.value !== null)
        .map((fact) => ({
          key: fact.key,
          label: fact.label,
          value: `${fact.value}${fact.unit ? ` ${fact.unit}` : ""}`,
        })),
    );
  }
  if (detail) {
    if (property.numberOfRooms > 0 && compact.family !== "land")
      facts.push({
        key: "rooms",
        label: "Antal rum",
        value: formatNumber(property.numberOfRooms),
      });
    if (property.yearBuilt > 0)
      facts.push({
        key: "year",
        label: "Byggeår",
        value: [property.yearBuilt, property.yearRenovationAddition]
          .filter((year) => year > 0)
          .join(" / "),
      });
  }
  if (!property.isSold) {
    if (property.priceInfo)
      facts.push({
        key: "price",
        label: property.isRental ? "Leje" : "Kontant",
        value: property.priceInfo,
      });
    if (detail && property.ownerExpensesTotalMonthly > 0 && !property.isRental)
      facts.push({
        key: "expenses",
        label: "Ejerudgift pr. md.",
        value: `${formatNumber(property.ownerExpensesTotalMonthly)} kr.`,
      });
  }
  if (detail) {
    facts.push({
      key: "case",
      label: "Sagsnummer",
      value: property.caseNumber,
    });
    if (property.energyLabel)
      facts.push({
        key: "energy",
        label: "Energimærkning",
        value: property.energyLabel,
      });
  }
  return facts;
}
